import { ref } from 'vue'
import { defineStore } from 'pinia'
import {
  collection,
  addDoc,
  updateDoc,
  doc,
  onSnapshot,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
  type Unsubscribe,
} from 'firebase/firestore'
import { db } from '@/firebase'
import { useAuthStore } from './auth'
import { writeAuditLog } from './auditLog'
import type { PriorityTrafficIncident } from '@/types'

export const usePriorityTrafficStore = defineStore('priorityTraffic', () => {
  const activePti = ref<PriorityTrafficIncident | null>(null)
  const resolvedPtis = ref<PriorityTrafficIncident[]>([])
  const usedEntryIds = ref<Set<string>>(new Set())
  const isLoading = ref(false)

  let unsubscribe: Unsubscribe | null = null
  let _incidentId = ''
  let _opId = ''

  function subscribeToPriorityTraffic(incidentId: string, opId: string) {
    if (incidentId === _incidentId && opId === _opId && unsubscribe) return
    if (unsubscribe) unsubscribe()
    _incidentId = incidentId
    _opId = opId

    isLoading.value = true
    activePti.value = null
    resolvedPtis.value = []
    usedEntryIds.value = new Set()

    unsubscribe = onSnapshot(
      collection(db, 'incidents', incidentId, 'operational_periods', opId, 'priority_traffic'),
      (snap) => {
        const ids = new Set<string>()
        let found: PriorityTrafficIncident | null = null
        const resolved: PriorityTrafficIncident[] = []
        for (const d of snap.docs) {
          const data = d.data()
          if (data.triggering_radio_log_entry_id) ids.add(data.triggering_radio_log_entry_id)
          if (data.status === 'active') found = { pti_id: d.id, ...data } as PriorityTrafficIncident
          if (data.status === 'resolved') resolved.push({ pti_id: d.id, ...data } as PriorityTrafficIncident)
        }
        activePti.value = found
        resolvedPtis.value = resolved.sort((a, b) => (b.resolved_at?.toMillis() ?? 0) - (a.resolved_at?.toMillis() ?? 0))
        usedEntryIds.value = ids
        isLoading.value = false
      },
    )
  }

  function unsubscribeFromPriorityTraffic() {
    if (unsubscribe) {
      unsubscribe()
      unsubscribe = null
    }
  }

  async function createPti(
    incidentId: string,
    opId: string,
    payload: {
      declared_by_team_id: string
      declared_by_team_identifier: string
      triggering_radio_log_entry_id: string | null
    },
  ) {
    const docRef = await addDoc(
      collection(db, 'incidents', incidentId, 'operational_periods', opId, 'priority_traffic'),
      {
        status: 'active',
        declared_at: serverTimestamp(),
        declared_by_team_id: payload.declared_by_team_id,
        declared_by_team_identifier: payload.declared_by_team_identifier,
        triggering_radio_log_entry_id: payload.triggering_radio_log_entry_id,
        location_address: null,
        location_coordinates: null,
        resources_requested: [],
        notes: null,
        resolved_at: null,
        resolved_by_user_id: null,
        resolved_by_name: null,
      },
    )

    await writeAuditLog(incidentId, 'priority_traffic.declared', 'priority_traffic', docRef.id, null, {
      team_identifier: payload.declared_by_team_identifier,
    })

    return docRef.id
  }

  async function updatePtiField(
    incidentId: string,
    opId: string,
    ptiId: string,
    field: string,
    value: string | null,
  ) {
    const before = activePti.value
      ? { [field]: (activePti.value as Record<string, unknown>)[field] ?? null }
      : null

    await updateDoc(
      doc(db, 'incidents', incidentId, 'operational_periods', opId, 'priority_traffic', ptiId),
      { [field]: value },
    )

    await writeAuditLog(incidentId, 'priority_traffic.field_updated', 'priority_traffic', ptiId, before, { [field]: value })
  }

  async function toggleResource(
    incidentId: string,
    opId: string,
    ptiId: string,
    resource: string,
  ) {
    const current = activePti.value?.resources_requested ?? []
    const isSelected = current.includes(resource)

    await updateDoc(
      doc(db, 'incidents', incidentId, 'operational_periods', opId, 'priority_traffic', ptiId),
      { resources_requested: isSelected ? arrayRemove(resource) : arrayUnion(resource) },
    )

    await writeAuditLog(
      incidentId,
      'priority_traffic.resource_toggled',
      'priority_traffic',
      ptiId,
      { resources_requested: current },
      { resource, action: isSelected ? 'removed' : 'added' },
    )
  }

  async function resolvePti(incidentId: string, opId: string, ptiId: string) {
    const authStore = useAuthStore()
    const user = authStore.currentUser
    const profile = authStore.userProfile
    const userName = profile
      ? `${profile.user_first_name} ${profile.user_last_name}`.trim()
      : 'Unknown'

    await updateDoc(
      doc(db, 'incidents', incidentId, 'operational_periods', opId, 'priority_traffic', ptiId),
      {
        status: 'resolved',
        resolved_at: serverTimestamp(),
        resolved_by_user_id: user?.uid ?? null,
        resolved_by_name: userName,
      },
    )

    await writeAuditLog(
      incidentId,
      'priority_traffic.resolved',
      'priority_traffic',
      ptiId,
      { status: 'active' },
      { status: 'resolved', resolved_by_name: userName },
    )
  }

  function teardown() {
    if (unsubscribe) {
      unsubscribe()
      unsubscribe = null
    }
    _incidentId = ''
    _opId = ''
    activePti.value = null
    resolvedPtis.value = []
    usedEntryIds.value = new Set()
    isLoading.value = false
  }

  return {
    activePti,
    resolvedPtis,
    usedEntryIds,
    isLoading,
    subscribeToPriorityTraffic,
    unsubscribeFromPriorityTraffic,
    createPti,
    updatePtiField,
    toggleResource,
    resolvePti,
    teardown,
  }
})
